<template>
  <VRow class="dashboard-page">
    <VCol cols="12" class="dashboard-page__section">
      <DashboardAnnouncementBanner
        :announcement="dashboardAnnouncement"
        :loading="isAnnouncementLoading"
      />
    </VCol>

    <VCol cols="12" class="dashboard-page__section">
      <DashboardStatCards :stats="dashboardStats" :loading="isStatsLoading" />
    </VCol>

    <VCol cols="12" class="dashboard-page__section">
      <DashboardQuickAccess
        :items="quickAccessItems"
        @select="onQuickAccessSelect"
      />
    </VCol>

    <VCol cols="12" md="7" class="dashboard-page__section">
      <DashboardCalendarCard
        :reminders="reminders"
        :markedDateKeys="markedDateKeys"
        :holidayDateKeys="holidayDateKeys"
        :loading="isRemindersLoading"
        :mobile="mobile"
        @update:selected-date-key="selectedDateKey = $event ?? getTodayKey()"
        @update:view-month="viewMonth = $event"
      />
    </VCol>

    <VCol cols="12" md="5" class="dashboard-page__section">
      <DashboardRecentNotifications
        :notifications="recentNotifications"
        :loading="isNotificationsLoading"
      />
    </VCol>
  </VRow>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { VCol, VRow } from 'vuetify/components'
import DashboardAnnouncementBanner from './DashboardAnnouncementBanner/DashboardAnnouncementBanner.vue'
import DashboardCalendarCard from './DashboardCalendarCard/DashboardCalendarCard.vue'
import {
  DASHBOARD_ANNOUNCEMENT_EMPTY_BODY,
  DASHBOARD_QUICK_ACCESS_ITEMS,
  DASHBOARD_STAT_CARDS,
} from './DashboardPage.constants'
import DashboardQuickAccess from './DashboardQuickAccess/DashboardQuickAccess.vue'
import DashboardRecentNotifications from './DashboardRecentNotifications/DashboardRecentNotifications.vue'
import DashboardStatCards from './DashboardStatCards/DashboardStatCards.vue'
import { useUnreadArticlesCountQuery } from '@/modules/Article/requests/queries/useUnreadArticlesCountQuery'
import { useResponsiveLayout } from '@/modules/Core/composables'
import { useToastStore } from '@/modules/Core/store'
import type { DashboardQuickAccessItem } from '@/modules/Dashboard/types/model'
import { useLatestEmployeeNotificationsQuery } from '@/modules/Notification/requests/queries/useLatestEmployeeNotificationsQuery'
import { mapLatestEmployeeNotificationToApp } from '@/modules/Notification/utils/employeeNotificationMappers'
import { useRemindersList } from '@/modules/Reminder/composables/useRemindersList'
import { useRemindersCountQuery } from '@/modules/Reminder/requests/queries/useRemindersCountQuery'
import {
  getDateKeyRange,
  getJalaliMonthRange,
  getJalaliParts,
  getTodayKey,
} from '@/modules/Reminder/utils/jalaliCalendar'
import { useLetterSummaryQuery } from '@/modules/Secretariat/requests/queries/useLetterSummaryQuery'

defineOptions({
  name: 'DashboardPage',
})

const router = useRouter()
const toastStore = useToastStore()
const { mobile } = useResponsiveLayout()

const quickAccessItems = DASHBOARD_QUICK_ACCESS_ITEMS
const holidayDateKeys: string[] = []

const { data: letterSummary, isFetching: isLetterSummaryFetching } =
  useLetterSummaryQuery()

const todayRange = computed(() => getDateKeyRange(getTodayKey()))

const { data: todayRemindersCountDto, isFetching: isTodayRemindersFetching } =
  useRemindersCountQuery(todayRange)

const { data: unreadArticlesDto, isFetching: isUnreadArticlesFetching } =
  useUnreadArticlesCountQuery()

const selectedDateKey = ref(getTodayKey())
const viewMonth = ref<{ year: number; month: number }>(getJalaliParts())

const {
  reminders,
  markedDateKeys,
  isFetching: isRemindersFetching,
} = useRemindersList(
  () => getJalaliMonthRange(viewMonth.value.year, viewMonth.value.month),
  selectedDateKey
)

const selectedDayRange = computed(() => getDateKeyRange(selectedDateKey.value))

const { isFetching: isSelectedDayRemindersCountFetching } =
  useRemindersCountQuery(selectedDayRange)

const {
  data: latestNotificationsDto,
  isFetching: isLatestNotificationsFetching,
} = useLatestEmployeeNotificationsQuery()

const recentNotifications = computed(() =>
  (latestNotificationsDto.value ?? []).map(mapLatestEmployeeNotificationToApp)
)

const isStatsLoading = computed(
  () =>
    isLetterSummaryFetching.value ||
    isTodayRemindersFetching.value ||
    isUnreadArticlesFetching.value
)

const isNotificationsLoading = computed(
  () => isLatestNotificationsFetching.value
)

const isAnnouncementLoading = isNotificationsLoading

const isRemindersLoading = computed(
  () => isRemindersFetching.value || isSelectedDayRemindersCountFetching.value
)

const dashboardAnnouncement = computed(() => {
  const latest = latestNotificationsDto.value?.[0]

  if (!latest) {
    return {
      title: 'اطلاعیه',
      body: DASHBOARD_ANNOUNCEMENT_EMPTY_BODY,
      isUnread: false,
    }
  }

  return {
    title: latest.title,
    body: latest.body,
    isUnread: latest.readAt == null,
  }
})

const dashboardStats = computed(() => {
  const unreadLetters = letterSummary.value?.unreadCount ?? 0
  const lettersInCirculation = letterSummary.value?.sentCount ?? 0
  const todayReminders = todayRemindersCountDto.value?.count ?? 0
  const unreadArticles = unreadArticlesDto.value?.count ?? 0

  return DASHBOARD_STAT_CARDS.map((stat) => {
    if (stat.id === 'unread-letters') {
      return { ...stat, value: unreadLetters }
    }
    if (stat.id === 'letters-in-circulation') {
      return { ...stat, value: lettersInCirculation }
    }
    if (stat.id === 'today-reminders') {
      return { ...stat, value: todayReminders }
    }
    if (stat.id === 'unread-articles') {
      return { ...stat, value: unreadArticles }
    }
    return stat
  })
})

function onQuickAccessSelect(item: DashboardQuickAccessItem) {
  if (item.action === 'toast') {
    toastStore.showSuccessToast(item.toastMessage ?? 'به‌زودی')
    return
  }

  if (item.to) {
    router.push(item.to)
  }
}
</script>

<style scoped lang="scss">
.dashboard-page {
  min-height: 100%;
  margin: -8px;
}

.dashboard-page__section {
  padding: 8px;
}

@media (max-width: 959px) {
  .dashboard-page {
    padding-bottom: 8px;
  }
}
</style>
